import { camelCase, isArray, isNull, isObject } from 'lodash';

/**
 * Utility that accepts a string and formats any currency/numbers within the string, adding commas where appropriate and stripping decimals. Example:
 * ```jsx
 * formatCurrency('Up to $5250.00 funded per year'); => returns 'Up to $5,250 funded per year'
 * ```
 * @packageDocumentation
 */

export const formatCurrency = (str: string) => {
  return str.replace(/\B(?=(\d{3})+(?!\d))/g, ',').replace(/\.[0-9][0-9]/g, '');
};

/**
 * Utility that replaces a valid 10-digit phone number string (with or without leading +1) into the
 * format: (xxx) xxx-xxxx.
 * Example:
 * ```jsx
 * formatPhone('123-456-7890'); => returns '(123) 456-7890'
 * ```
 * @packageDocumentation
 */
export const formatPhone = (str = '') => {
  // strip +1 if present, remove all non-digit characters, take last 10 digits
  const cleanedString = str.replace('+1', '').replace(/\D+/g, '').slice(0, 10);

  return cleanedString && cleanedString.length === 10
    ? cleanedString.replace(/([0-9]{1,3})([0-9]{3})([0-9]{4}$)/gi, '($1) $2-$3')
    : '';
};

/**
 * Utility to join an array of values with and and separating the last value. Example:
 * ```jsx
 * formatCommaSeparated(['one', 'two']); => returns 'one and two'
 * formatCommaSeparated(['one', 'two', 'three']); => returns 'one, two and three'
 * formatCommaSeparated(['one', 'two', 'three', 'four'], 3); => returns 'one, two, three and more'
 * ```
 * @packageDocumentation
 */
export const formatCommaSeparated = (values: string[] = [], max = 3) => {
  if (!values.length || values.length === 1) return values[0] ?? '';

  const lastIndex = (values.length > max ? max : values.length) - 1;

  const start = values.slice(0, lastIndex).join(', ');
  const end = values.length > max ? `${values[lastIndex]} and more` : `and ${values[lastIndex]}`;

  return `${start}${values.length > max ? ',' : ''} ${end}`;
};

/**
 * Utility that accepts a single digit integer and returns the integer as a word. Example:
 * ```jsx
 * formatNumberToWord(2); => returns 'two'
 * formatNumberToWord(4, 2); => returns 'more than two'
 * ```
 * @packageDocumentation
 */
export const formatNumberToWord = (num = 0, max = 3) => {
  const wordNumbers = ['', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine'];

  if (num > max) {
    return `more than ${wordNumbers[max]}`;
  }

  return wordNumbers[num];
};

/**
 * Utility that creates the correct possessive form of an EP name. Example:
 * ```jsx
 * makePossessive("Lowe's"); => returns "Lowe's"
 * makePossessive('Guild for Guilders'); => returns "Guild for Guilders'"
 * makePossessive('Chipotle'); => returns "Chipotle's"
 * ```
 * @packageDocumentation
 */
export const makePossessive = (employerName: string | undefined) => {
  if (employerName?.slice(-2) === "'s") {
    return employerName;
  } else if (employerName?.slice(-1) === 's') {
    return `${employerName}'`;
  }
  return `${employerName}'s`;
};

type AnyObject = { [key: string]: any };

/**
 * Converts all keys in an object to camelCase
 * @param obj any object
 * @returns the passed in object with all keys converted to camelCase
 */
export function convertKeysToCamelCase(obj: AnyObject): AnyObject {
  if (isNull(obj) || !isObject(obj)) {
    return obj;
  }

  if (isArray(obj)) {
    return obj.map(convertKeysToCamelCase);
  }

  return Object.entries(obj).reduce((acc: AnyObject, [key, value]: [string, any]) => {
    const camelCaseKey = camelCase(key);

    if (isObject(value) && !isNull(value)) {
      acc[camelCaseKey] = convertKeysToCamelCase(value);
    } else {
      acc[camelCaseKey] = value;
    }

    return acc;
  }, {});
}
